// src/pages/Paths.tsx

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import PathGraph from '@/components/PathGraph';
import { Button } from '@/components/ui/button';
import { Plus, FileText, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from '@/components/ui/switch';
import CoverUploader from '@/components/CoverUploader';

const Paths = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPathTitle, setNewPathTitle] = useState('');
  const [newPathPublic, setNewPathPublic] = useState(true);
  const [isEditingPath, setIsEditingPath] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingPublic, setEditingPublic] = useState(true);

  const { data: paths = [] } = useQuery({
    queryKey: ['paths'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paths')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: nodes = [] } = useQuery({
    queryKey: ['path-nodes', selectedPathId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('path_nodes')
        .select('*')
        .eq('path_id', selectedPathId)
        .order('order_index');
      if (error) throw error;
      return data;
    },
    enabled: !!selectedPathId,
  });

  const { data: collections = [] } = useQuery({
    queryKey: ['collections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collections')
        .select('*');
      if (error) throw error;
      return data;
    },
  });

  const { data: collectionVideos = [] } = useQuery({
    queryKey: ['collection-videos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_videos')
        .select('*');
      if (error) throw error;
      return data;
    },
  });

  const selectedPath = paths.find(p => p.id === selectedPathId);

  const createPath = useMutation({
    mutationFn: async (payload: { title: string; is_public: boolean }) => {
      const { data, error } = await supabase
        .from('paths')
        .insert([{ title: payload.title, user_id: user?.id, is_public: payload.is_public }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['paths']);
      setCreateDialogOpen(false);
      setNewPathTitle('');
      toast({ title: 'Path created' });
      setSelectedPathId(data.id);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updatePathTitle = useMutation({
    mutationFn: async ({ id, title, is_public }: { id: string; title: string; is_public: boolean }) => {
      const { error } = await supabase
        .from('paths')
        .update({ title, is_public })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['paths']);
      setIsEditingPath(false);
      toast({ title: 'Path updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleCreatePath = () => {
    if (!newPathTitle.trim()) {
      toast({ title: 'Error', description: 'Please enter a title', variant: 'destructive' });
      return;
    }
    createPath.mutate({ title: newPathTitle.trim(), is_public: newPathPublic });
  };

  const handleSavePathSettings = () => {
    if (!selectedPathId || !editingTitle.trim()) return;
    updatePathTitle.mutate({ id: selectedPathId, title: editingTitle.trim(), is_public: editingPublic });
  };

  const filteredCollections = collections.filter((c) =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create New Path
          </Button>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Learning Path</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <Input
                placeholder="Enter path title"
                value={newPathTitle}
                onChange={(e) => setNewPathTitle(e.target.value)}
              />
              <div className="flex items-center space-x-2">
                <Switch checked={newPathPublic} onCheckedChange={setNewPathPublic} />
                <span>Make path public</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePath}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditingPath} onOpenChange={setIsEditingPath}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Path Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Visibility</label>
                <div className="flex items-center space-x-2">
                  <Switch checked={editingPublic} onCheckedChange={setEditingPublic} />
                  <span>Make path public</span>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Cover Image</label>
                <CoverUploader pathId={selectedPathId!} onUploaded={setCoverUrl} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditingPath(false)}>Cancel</Button>
              <Button onClick={handleSavePathSettings}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paths.map((path) => (
            <Card key={path.id} className="bg-card border-border">
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-lg">{path.title}</h3>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedPathId(path.id);
                      setCoverUrl(path.cover_image_url ?? null);
                      setEditingTitle(path.title);
                      setEditingPublic(path.is_public);
                    }}
                  >
                    Open
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        {selectedPathId && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50">
            <div className="container mx-auto p-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <Button variant="ghost" onClick={() => setSelectedPathId(null)}>
                  ← Back to All Paths
                </Button>
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold">
                    {selectedPath?.title}
                  </h2>
                  <Button onClick={() => setIsEditingPath(true)}>
                    <Settings className="w-4 h-4 mr-2" /> Edit Path
                  </Button>
                  <Button onClick={async () => {
                    await supabase.from('path_nodes').insert([{
                      path_id: selectedPathId,
                      title: 'New Topic',
                      type: 'text',
                      position_x: 100,
                      position_y: 100
                    }]);
                    queryClient.invalidateQueries(['path-nodes', selectedPathId]);
                    toast({ title: 'Text block added' });
                  }}>
                    <FileText className="w-4 h-4 mr-2" /> Add Text Block
                  </Button>
                </div>
              </div>
              <div className="flex gap-4 flex-1">
                <div className="w-3/4">
                  <PathGraph
                    nodes={nodes}
                    collectionVideos={collectionVideos}
                    selectedPathId={selectedPathId}
                    collections={collections}
                  />
                </div>
                <div className="w-1/4">
                  <Input
                    type="text"
                    placeholder="Search collections..."
                    className="mb-2"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {filteredCollections.map((collection) => {
                      const preview = collectionVideos.find(
                        (v) => v.collection_id === collection.id
                      );
                      return (
                        <div
                          key={collection.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('collection_id', collection.id);
                            e.dataTransfer.setData('collection_title', collection.name);
                          }}
                          className="p-2 rounded cursor-pointer border hover:bg-accent flex flex-col gap-1"
                        >
                          <div className="font-medium">{collection.name}</div>
                          {preview && (
                            <img
                              src={preview.thumbnail}
                              alt={preview.title}
                              className="w-full h-20 object-cover rounded border"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Paths;